import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { Shield, Download, ChevronDown, ChevronRight, Activity, Users, Layers, BarChart3 } from "lucide-react";

// ─── JSON Diff Viewer ─────────────────────────────────────────────────────────

function JsonDiffView({ before, after }: { before: any; after: any }) {
  const beforeStr = before ? JSON.stringify(before, null, 2) : null;
  const afterStr = after ? JSON.stringify(after, null, 2) : null;

  if (!beforeStr && !afterStr) {
    return <p className="text-xs text-muted-foreground italic">Aucune donnée before/after</p>;
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 text-xs font-mono">
      <div>
        <p className="text-muted-foreground mb-1 font-sans text-[11px] uppercase tracking-wide">Avant</p>
        <pre className="bg-destructive/5 border border-destructive/20 rounded p-2 overflow-auto max-h-40 text-foreground">
          {beforeStr ?? "—"}
        </pre>
      </div>
      <div>
        <p className="text-muted-foreground mb-1 font-sans text-[11px] uppercase tracking-wide">Après</p>
        <pre className="bg-success/5 border border-success/20 rounded p-2 overflow-auto max-h-40 text-foreground">
          {afterStr ?? "—"}
        </pre>
      </div>
    </div>
  );
}

// ─── Log Row ──────────────────────────────────────────────────────────────────

function LogRow({ log }: { log: any }) {
  const [expanded, setExpanded] = useState(false);
  const actor = log.User
    ? `${[log.User.firstName, log.User.lastName].filter(Boolean).join(" ") || log.User.email}`
    : (log.actorStaffUserId ?? "Système");

  return (
    <>
      <tr
        className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="p-3 text-muted-foreground">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
        <td className="p-3">
          <Badge variant="outline" className="text-xs font-mono">{log.action}</Badge>
        </td>
        <td className="p-3 text-sm">{actor}</td>
        <td className="p-3">
          <span className="text-xs text-muted-foreground">{log.entityType}</span>
        </td>
        <td className="p-3 text-xs text-muted-foreground truncate max-w-[120px]">{log.companyId ?? "—"}</td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/10">
          <td colSpan={6} className="p-4">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                <strong>Entity ID:</strong> {log.entityId ?? "—"} · <strong>Company:</strong> {log.companyId ?? "—"}
              </p>
              <JsonDiffView before={log.before} after={log.after} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function AuditTrail() {
  const [page, setPage] = useState(1);
  const [filterAction, setFilterAction] = useState("");
  const [filterEntityType, setFilterEntityType] = useState("");
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [draftAction, setDraftAction] = useState("");
  const [draftEntityType, setDraftEntityType] = useState("");
  const [draftCompanyId, setDraftCompanyId] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data: logs, isLoading } = trpc.admin.audit.list.useQuery({
    page,
    limit: 25,
    action: filterAction || undefined,
    entityType: filterEntityType || undefined,
    companyId: filterCompanyId || undefined,
  });

  const { data: stats } = trpc.admin.audit.stats.useQuery({ days: 30 });

  const exportQuery = trpc.admin.audit.export.useQuery(
    {
      action: filterAction || undefined,
      entityType: filterEntityType || undefined,
      companyId: filterCompanyId || undefined,
    },
    { enabled: false }
  );

  const totalPages = Math.ceil((logs?.total ?? 0) / 25);

  const applyFilters = () => {
    setFilterAction(draftAction);
    setFilterEntityType(draftEntityType);
    setFilterCompanyId(draftCompanyId);
    setPage(1);
  };

  const clearFilters = () => {
    setDraftAction("");
    setDraftEntityType("");
    setDraftCompanyId("");
    setFilterAction("");
    setFilterEntityType("");
    setFilterCompanyId("");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportQuery.refetch();
      if (result.data) {
        const blob = new Blob([result.data.csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.data.filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Audit Trail</h1>
            <p className="text-muted-foreground">Journal global des actions staff et système — 360° de la plateforme</p>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Export…" : "Export CSV"}
          </Button>
        </div>

        {/* Stats — 30 jours */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Volume (30j)</CardTitle>
              <Activity className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalVolume?.toLocaleString("fr-FR") ?? "—"}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Action #1</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-bold truncate">{stats?.topActions?.[0]?.action ?? "—"}</div>
              <p className="text-xs text-muted-foreground">{stats?.topActions?.[0]?.count ?? 0} fois</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Tenant #1</CardTitle>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-bold truncate">{stats?.topTenants?.[0]?.companyName ?? "—"}</div>
              <p className="text-xs text-muted-foreground">{stats?.topTenants?.[0]?.count ?? 0} actions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Staff #1</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-bold truncate">{stats?.topStaff?.[0]?.name ?? "—"}</div>
              <p className="text-xs text-muted-foreground">{stats?.topStaff?.[0]?.count ?? 0} actions</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Action</label>
                <Input
                  placeholder="ex: COMPANY_UPDATE"
                  value={draftAction}
                  onChange={(e) => setDraftAction(e.target.value)}
                  className="h-8 text-sm w-44"
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Type entité</label>
                <Input
                  placeholder="ex: Company"
                  value={draftEntityType}
                  onChange={(e) => setDraftEntityType(e.target.value)}
                  className="h-8 text-sm w-36"
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Company ID</label>
                <Input
                  placeholder="UUID tenant"
                  value={draftCompanyId}
                  onChange={(e) => setDraftCompanyId(e.target.value)}
                  className="h-8 text-sm w-52"
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </div>
              <Button size="sm" onClick={applyFilters}>Filtrer</Button>
              {(filterAction || filterEntityType || filterCompanyId) && (
                <Button size="sm" variant="ghost" onClick={clearFilters}>Réinitialiser</Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 w-8" />
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Action</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Acteur</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Entité</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Tenant</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-3"><Skeleton className="h-4 w-4" /></td>
                        <td className="p-3"><Skeleton className="h-4 w-32" /></td>
                        <td className="p-3"><Skeleton className="h-5 w-36" /></td>
                        <td className="p-3"><Skeleton className="h-4 w-28" /></td>
                        <td className="p-3"><Skeleton className="h-4 w-20" /></td>
                        <td className="p-3"><Skeleton className="h-4 w-24" /></td>
                      </tr>
                    ))
                  ) : !logs?.items.length ? (
                    <tr>
                      <td colSpan={6} className="p-10 text-center text-muted-foreground">
                        <Shield className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                        <p>Aucun log trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    logs.items.map((log: any) => <LogRow key={log.id} log={log} />)
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page} / {totalPages} — {logs?.total ?? 0} logs
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
